import { useRef } from "react";
import { Config } from "@imgly/background-removal";
import imageHelper from "components/helper/imageHelper";

const MAX_IMG_SIZE = 1280;
const MAX_IMG_WEIGHT = 200;

type StartParams = {
    image: HTMLImageElement;
    area: any;
    config: Config;
    onSuccess: (blob: Blob) => void;
    onError: (err: any) => void;
};

export const useBackgroundRemovalWorker = () => {
    const workerRef = useRef<Worker | null>(null);
    const cancelledRef = useRef(false);

    const startWorker = async ({
        image,
        area,
        config,
        onSuccess,
        onError,
    }: StartParams) => {
        cancelledRef.current = false;

        try {
            const baseBlob = await imageHelper.processImage(
                image,
                area,
                MAX_IMG_SIZE,
                MAX_IMG_WEIGHT,
            );

            if (cancelledRef.current) return;

            workerRef.current = new Worker(
                new URL("./backgroundRemoval.worker.ts", import.meta.url),
                {
                    type: "module",
                },
            );

            workerRef.current.onmessage = (e) => {
                if (cancelledRef.current) return;

                if (e.data.progress) return;

                const { result, error } = e.data;

                workerRef.current?.terminate();
                workerRef.current = null;

                if (error) onError(error);
                else onSuccess(result);
            };

            workerRef.current.onerror = (err) => {
                if (cancelledRef.current) return;

                workerRef.current?.terminate();
                workerRef.current = null;
                onError(err);
            };

            workerRef.current.postMessage({ blob: baseBlob, config });
        } catch (err) {
            if (!cancelledRef.current) {
                onError(err);
            }
        }
    };

    const cancelWorker = () => {
        cancelledRef.current = true;
        workerRef.current?.terminate();
        workerRef.current = null;
    };

    return { startWorker, cancelWorker };
};
